/**
 * The XML parser and serializer the redline package runs on.
 *
 * The package is written against the browser's `DOMParser` and `XMLSerializer`.
 * In Word's webview they exist. On a server (the web's redline routes, a Node
 * script) they do not, and the TypeScript types for browser globals are always
 * present, so nothing fails at build time: `new DOMParser()` throws at run
 * time, and inside a try/catch that read as "nothing to change" and the engine
 * handed the paragraph back untouched with no error anywhere. Every parse and
 * serialisation in the package goes through this class so that a missing host
 * fails loudly, at the first call, with the fix in the message.
 *
 * A server installs xmldom's pair before the first call:
 *   XmlHost.install({ DOMParser, XMLSerializer })   from "@xmldom/xmldom"
 */

/** The two constructors a host provides; the browser's own, or xmldom's on a server. */
export interface XmlHostGlobals {
    DOMParser: typeof DOMParser;
    XMLSerializer: typeof XMLSerializer;
}

/**
 * Raised when the package is asked to parse or serialise and the running host
 * has no `DOMParser` or `XMLSerializer`: server-side code imported the package
 * without installing xmldom's. Nothing else can go wrong here, so the message
 * names the fix.
 */
export class XmlHostMissingError extends Error {
    constructor(missingGlobalName: keyof XmlHostGlobals) {
        super(
            `${missingGlobalName} is not defined: the redline package needs the browser's XML host. On a server call XmlHost.install({ DOMParser, XMLSerializer }) from "@xmldom/xmldom" before the first redline call.`,
        );
        this.name = "XmlHostMissingError";
    }
}

export class XmlHost {
    /**
     * Makes xmldom's constructors the package's host, when the running host has
     * none of its own. A browser's own pair is left in place, so the add-in and a
     * jsdom test keep the DOM they run in.
     */
    static install(hostGlobals: XmlHostGlobals): void {
        const runningHost = globalThis as Partial<XmlHostGlobals>;
        runningHost.DOMParser ??= hostGlobals.DOMParser;
        runningHost.XMLSerializer ??= hostGlobals.XMLSerializer;
    }

    /** A parser from the running host. Throws `XmlHostMissingError` when there is none. */
    static domParser(): DOMParser {
        const runningHost = globalThis as Partial<XmlHostGlobals>;
        /**
         * WHEN the host has no DOMParser constructor
         *   CONSEQUENCE
         *     Fail with the install instruction rather than let a ReferenceError
         *     surface deep inside a parse, or be swallowed by a caller's catch.
         */
        if (typeof runningHost.DOMParser !== "function") throw new XmlHostMissingError("DOMParser");
        return new runningHost.DOMParser();
    }

    /** A serializer from the running host. Throws `XmlHostMissingError` when there is none. */
    static xmlSerializer(): XMLSerializer {
        const runningHost = globalThis as Partial<XmlHostGlobals>;
        /**
         * WHEN the host has no XMLSerializer constructor
         *   CONSEQUENCE
         *     Fail with the install instruction, the same way as for the parser.
         */
        if (typeof runningHost.XMLSerializer !== "function") throw new XmlHostMissingError("XMLSerializer");
        return new runningHost.XMLSerializer();
    }
}
